
import React from "react";
import { Link } from "react-router-dom";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  CardDescription,
  CardFooter 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnhancedGiftTracking } from "@/types/custom-types";
import { formatDate, cn } from "@/lib/utils";
import { Gift, Check, Clock, TruckIcon, CalendarDays, ExternalLink, Users } from "lucide-react";

interface EnhancedGiftTrackingCardProps {
  gift: EnhancedGiftTracking;
  showContact?: boolean;
  showOrganization?: boolean;
  className?: string;
}

export function EnhancedGiftTrackingCard({
  gift,
  showContact = false,
  showOrganization = true,
  className
}: EnhancedGiftTrackingCardProps) {
  // Function to get shipping status badge color
  const getStatusBadgeClass = (status?: string) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case 'delivered':
        return "bg-green-100 text-green-800";
      case 'shipped':
        return "bg-blue-100 text-blue-800";
      case 'in transit':
        return "bg-indigo-100 text-indigo-800";
      case 'preparing':
        return "bg-amber-100 text-amber-800";
      case 'processing':
        return "bg-orange-100 text-orange-800";
      case 'cancelled':
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader className="pb-2">
        {gift.shipping_status && (
          <Badge className={cn("self-start", getStatusBadgeClass(gift.shipping_status))}>
            {gift.shipping_status}
          </Badge>
        )}
        <CardTitle className="flex items-center gap-2 mt-1">
          <Gift className="h-4 w-4" />
          {gift.gift?.gift_name || "Gift"}
        </CardTitle>
        <CardDescription>
          {gift.gift?.category && `${gift.gift.category} • `}
          {gift.gift?.cost && `$${gift.gift.cost}`}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pb-0 space-y-3">
        {/* Recipient Information */}
        {(showContact || showOrganization) && (
          <div className="text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 opacity-70" />
              <div>
                <span className="font-medium">Recipient:</span>{" "}
                {showContact && gift.contact && (
                  <span>
                    {gift.contact.first_name} {gift.contact.last_name}
                    {showOrganization && gift.organization && ` (${gift.organization.name})`}
                  </span>
                )}
                {!showContact && showOrganization && gift.organization && (
                  <span>{gift.organization.name}</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Date Information */}
        {gift.date_sent && (
          <div className="text-sm flex items-center gap-2">
            <CalendarDays className="h-4 w-4 opacity-70" />
            <div>
              <span className="font-medium">Sent:</span>{" "}
              {formatDate(gift.date_sent)}
            </div>
          </div>
        )}
        
        {/* Tracking Information */}
        {gift.tracking_number && (
          <div className="text-sm flex items-center gap-2">
            <TruckIcon className="h-4 w-4 opacity-70" />
            <div>
              <span className="font-medium">Tracking:</span>{" "}
              <span className="font-mono text-xs">
                {gift.tracking_number}
              </span>
            </div>
          </div>
        )}
        
        {/* Thank you status */}
        <div className="text-sm flex items-center gap-2">
          {gift.thank_you_received ? (
            <>
              <Check className="h-4 w-4 text-green-500" />
              <span>
                Thank you received
                {gift.thank_you_date && ` on ${formatDate(gift.thank_you_date)}`}
              </span>
            </>
          ) : (
            <>
              <Clock className="h-4 w-4 text-amber-500" />
              <span>No thank you received yet</span>
            </>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-3 mt-2">
        <Button asChild variant="ghost" size="sm" className="ml-auto">
          <Link to={`/gifts/${gift.id}`}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" />
            View Details
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
